/**
 * Formatting helpers. Money is integer paise everywhere in the API (SPEC §5);
 * these turn paise into the Indian-rupee strings the prototype shows.
 */

/** Group an integer with the Indian numbering system (1,05,000). */
function indianGroup(n: number): string {
  const s = String(n);
  if (s.length <= 3) return s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

/** Paise → "₹1,05,000" (no decimals; rounds paise to nearest rupee). */
export function inr(paise: number | null | undefined): string {
  if (paise == null || Number.isNaN(paise)) return '–';
  const rupees = Math.round(paise / 100);
  const sign = rupees < 0 ? '−' : '';
  return `${sign}₹${indianGroup(Math.abs(rupees))}`;
}

/** Signed paise → "−₹9,000" (used for TDS / balance deltas). */
export function inrSigned(paise: number | null | undefined): string {
  if (paise == null || Number.isNaN(paise)) return '–';
  const rupees = Math.round(paise / 100);
  if (rupees === 0) return '0';
  const sign = rupees < 0 ? '−' : '+';
  return `${sign}₹${indianGroup(Math.abs(rupees))}`;
}

/** Paise → compact "₹4.20L" / "₹90K" for tiles. */
export function inrShort(paise: number | null | undefined): { v: string; unit: string } {
  if (paise == null || Number.isNaN(paise)) return { v: '–', unit: '' };
  const rupees = Math.round(paise / 100);
  const abs = Math.abs(rupees);
  if (abs >= 100000) return { v: `₹${(rupees / 100000).toFixed(2)}`, unit: 'L' };
  if (abs >= 1000) return { v: `₹${Math.round(rupees / 1000)}`, unit: 'K' };
  return { v: `₹${indianGroup(rupees)}`, unit: '' };
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** epoch ms → "1 Apr 2025". */
export function fmtDate(epochMs: number | null | undefined): string {
  if (epochMs == null) return '–';
  const d = new Date(epochMs);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "YYYY-MM" → "August 2026". */
export function fmtPeriod(period: string | null | undefined): string {
  if (!period) return '–';
  const [y, m] = period.split('-');
  const idx = Number(m) - 1;
  if (idx < 0 || idx > 11) return period;
  return `${MONTHS_LONG[idx]} ${y}`;
}

/** "YYYY-MM" → "Aug 2026" (compact). */
export function fmtPeriodShort(period: string | null | undefined): string {
  if (!period) return '–';
  const [y, m] = period.split('-');
  const idx = Number(m) - 1;
  if (idx < 0 || idx > 11) return period;
  return `${MONTHS[idx]} ${y}`;
}

/** Current period "YYYY-MM" (UTC). */
export function currentPeriod(now = Date.now()): string {
  const d = new Date(now);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Whole days from now until `epochMs` (negative = overdue). */
export function daysUntil(epochMs: number | null | undefined, now = Date.now()): number | null {
  if (epochMs == null) return null;
  return Math.round((epochMs - now) / 86_400_000);
}
